import { useState, useCallback, useMemo, useEffect } from 'react';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { Brand, Logo } from './components/Logo';
import { Ic, HeartFilled, Badge, inputStyle, primaryButtonStyle, secondaryButtonStyle, ghostButtonStyle } from './components/ui';
import { PropertyCard } from './components/PropertyCard';
// PropertyDetail.jsx kept as reference — detail now opens in new window via PropertyCard
import { SettingsModal, PaywallModal, CalculatorModal, AIAnalysisModal, SaveFilterModal, ProfileModal, ContactModal } from './components/Modals';
import { UFS, ESTADOS_CIDADES, TIPOS, MODALIDADES, BANCOS, FORMAS_PAGAMENTO, NIVEIS_RISCO, FILTER_KEY_MAP_REVERSE, formatBRL, formatDate } from './data/constants';

// Saved filters store long-form keys (cidade, tipo, banco, ...) to match the
// backend API; the App state uses short-form keys (ci, tp, bk, ...) for
// compact URLs. Reverse-map when applying a saved filter to the local state.
function fromApiFilters(apiForm) {
  const out = {};
  for (const [k, v] of Object.entries(apiForm ?? {})) {
    const shortKey = FILTER_KEY_MAP_REVERSE[k];
    if (!shortKey || v == null || v === '') continue;
    out[shortKey] = String(v);
  }
  return out;
}
import { listProperties } from './services/properties.js';
import { startEnrichmentJob, getEnrichedIds } from './services/batchAnalysis';
import { getCurrentUser, fetchMe, logout as authLogout } from './services/auth.js';
import { fetchFavorites, addFavorite, removeFavorite, fetchSavedFilters, createSavedFilter, deleteSavedFilter } from './services/userdata.js';

const PER_PAGE = 24;

function SiteFooter({ onOpenContact }) {
  return (
    <footer role="contentinfo" style={{ borderTop: '1px solid var(--bd)', marginTop: 60, padding: '28px 20px 36px', textAlign: 'center' }}>
      <p style={{ fontSize: 11, color: 'var(--t3)', maxWidth: 680, margin: '0 auto 14px', lineHeight: 1.65 }}>
        <strong style={{ color: 'var(--t2)' }}>Aviso:</strong>{' '}
        LupaLeilão não é uma plataforma de leilões nem intermediária de vendas. Apenas agregamos imóveis publicados
        por bancos e leiloeiros oficiais para facilitar sua pesquisa. Toda negociação deve ser feita diretamente
        no site da instituição responsável.
      </p>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 18, fontSize: 11, color: 'var(--t3)' }}>
        <button
          type="button"
          onClick={onOpenContact}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, color: 'var(--t2)', textDecoration: 'underline' }}
        >
          Contato
        </button>
        <span aria-hidden="true" style={{ opacity: .5 }}>·</span>
        <span>© {new Date().getFullYear()} LupaLeilão</span>
      </div>
    </footer>
  );
}

function AppInner() {
  const { dark } = useTheme();

  // Navigation
  const [view, setView] = useState('home');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('dc');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // Auth
  const [user, setUser] = useState(null);
  // Anonymous 3-search/day counter, persisted across reloads.
  // Key: YYYY-MM-DD in America/Sao_Paulo to match the backend rate limiter.
  const [searchCount, setSearchCount] = useState(() => {
    try {
      const today = new Date(Date.now() - 3 * 3_600_000).toISOString().slice(0, 10);
      const raw = localStorage.getItem('lupa_anon_search_count');
      if (!raw) return 0;
      const { date, count } = JSON.parse(raw);
      return date === today ? Number(count) || 0 : 0;
    } catch { return 0; }
  });
  useEffect(() => {
    const today = new Date(Date.now() - 3 * 3_600_000).toISOString().slice(0, 10);
    try { localStorage.setItem('lupa_anon_search_count', JSON.stringify({ date: today, count: searchCount })); } catch {}
  }, [searchCount]);
  const [showPaywall, setShowPaywall] = useState(false);
  // Which tab the auth modal opens on. 'login' for Entrar / session-expired
  // / search-limit prompts; 'plans' for the Assinar CTA.
  const [paywallTab, setPaywallTab] = useState('plans');
  const openLogin   = () => { setPaywallTab('login'); setShowPaywall(true); };
  const openSignup  = () => { setPaywallTab('plans'); setShowPaywall(true); };

  // Favorites
  const [favorites, setFavorites] = useState(new Set());
  // Full property objects for the user's favorites. Populated from the
  // /api/favorites response so the "Favoritos" view can render even
  // listings that are inativo (won't come back from /api/properties which
  // filters status='ativo'). Kept separate from the paginated `properties`
  // state so a search doesn't clobber it.
  const [favProps, setFavProps] = useState([]);
  function hydrateFavorites(items) {
    if (!Array.isArray(items)) return;
    // Backend returns full property rows with `.id` — old code mistakenly
    // read `.propertyId`, which made the Set store undefined and the heart
    // icon never filled for saved favorites.
    setFavorites(new Set(items.map((p) => p.id).filter(Boolean)));
    setFavProps(items);
  }
  // NOTE: toggleFav is defined BELOW the `properties` useState declaration
  // (line ~138) to avoid a TDZ reference — the useCallback dep array evaluates
  // `properties` on every render, so the state must exist by then.

  // Modals
  const [showCalc, setShowCalc] = useState(false);
  const [calcProp, setCalcProp] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const [aiProp, setAiProp] = useState(null);
  const [showSave, setShowSave] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showContact, setShowContact] = useState(false);

  // AI location enrichment (background job)
  const [enrichedIds, setEnrichedIds] = useState(() => getEnrichedIds());

  useEffect(() => {
    startEnrichmentJob([], (ids) => setEnrichedIds(ids));
  }, []);

  // API data
  const [properties, setProperties] = useState([]);
  const [apiTotal, setApiTotal] = useState(0);
  // Unfiltered total — fetched once on mount for the home page hero counter.
  // Keeping it separate from apiTotal prevents the home stats from drifting
  // every time the user applies a filter and then comes back to home.
  const [globalTotal, setGlobalTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [apiPage, setApiPage] = useState(1);

  // Toggle a favorite — deferred to here (after `properties` is declared) so
  // the useCallback dep array doesn't dereference it in the temporal dead zone.
  const toggleFav = useCallback((id) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      const removing = next.has(id);
      if (removing) { next.delete(id); removeFavorite(id); }
      else          { next.add(id);    addFavorite(id); }
      return next;
    });
    setFavProps((prev) => {
      if (prev.some((p) => p.id === id)) return prev.filter((p) => p.id !== id);
      const found = (properties ?? []).find((p) => p.id === id);
      if (found) return [...prev, found];
      fetchFavorites().then((items) => hydrateFavorites(items));
      return prev;
    });
  }, [properties]);

  // Fetch global total once for the home stats card. pageSize=1 keeps the
  // payload tiny — we only need the `total` count.
  useEffect(() => {
    listProperties({ pageSize: 1 })
      .then((res) => setGlobalTotal(res.total ?? 0))
      .catch(() => {});
  }, []);

  // Hydrate session on mount
  useEffect(() => {
    const cached = getCurrentUser();
    if (cached) {
      setUser(cached);
      fetchMe().then((u) => { if (u) setUser(u); });
      fetchFavorites().then((items) => { hydrateFavorites(items); });
      fetchSavedFilters().then((items) => { if (items.length) setSavedFilters(items); });
    }
    // /?upgrade=1 (used by /pagamento/cancelado and anywhere else that wants
    // to drop the user straight into the paywall) — auto-open, then strip.
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') === '1') {
      setShowPaywall(true);
      params.delete('upgrade');
      const clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.replaceState({}, '', clean);
    }
  }, []);

  // Handle session expiry (token refresh failed)
  useEffect(() => {
    const onExpired = () => { setUser(null); setFavorites(new Set()); setFavProps([]); setSavedFilters([]); setPaywallTab('login'); setShowPaywall(true); };
    window.addEventListener('lupa:auth-expired', onExpired);
    return () => window.removeEventListener('lupa:auth-expired', onExpired);
  }, []);

  // Filters
  const DEFAULT_FILTERS = { uf: '', ci: '', tp: '', md: '', bk: '', pm: '', px: '', qm: '', cd: '', nr: '' };
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const updateFilter = (key, val) => { setFilters((f) => ({ ...f, [key]: val })); setPage(1); };
  const cities = filters.uf ? (ESTADOS_CIDADES[filters.uf] || []) : [];

  // Search action
  const doSearch = () => {
    if (!user && searchCount >= 3) { setShowPaywall(true); return; }
    if (!user) setSearchCount((c) => c + 1);
    setView('results');
    setPage(1);
  };

  const SORT_MAP = { dc: 'desconto', pa: 'preco_asc', pd: 'preco_desc', dt: 'data', ar: 'area' };

  // Fetch from API when entering results view or when filters/sort change
  useEffect(() => {
    if (view !== 'results') return;
    setLoading(true);
    setApiPage(page); // Usar a página atual
    listProperties({
      uf: filters.uf || undefined,
      cidade: filters.ci || undefined,
      tipo: filters.tp || undefined,
      banco: filters.bk || undefined,
      modalidade: filters.md || undefined,
      formaPagamento: filters.cd || undefined,
      nivelRisco: filters.nr || undefined,
      precoMin: filters.pm || undefined,
      precoMax: filters.px || undefined,
      q: searchText || undefined,
      sort: SORT_MAP[sortBy] || 'desconto',
      page: page,
      pageSize: PER_PAGE,
    }).then((res) => {
      setProperties(res.items);
      setApiTotal(res.total);
    }).catch((err) => {
      // 429 daily_limit_exceeded (free tier cap) -> open the paywall so the
      // user can upgrade. Any other error: clear results silently.
      if (err?.status === 429 || err?.message === 'daily_limit_exceeded') {
        setProperties([]);
        setApiTotal(0);
        setShowPaywall(true);
      } else {
        setProperties([]);
        setApiTotal(0);
      }
    }).finally(() => setLoading(false));
  }, [view, filters, sortBy, searchText, page]);

  // Favorites view shows the full, persisted favorite list — including
  // inativos. Falls back to filtering the paginated results only if favProps
  // hasn't hydrated yet (shouldn't happen in practice).
  const favList = favProps.length > 0
    ? favProps
    : properties.filter((p) => favorites.has(p.id));
  const stats = { total: globalTotal || 29000, estados: 27, avgDiscount: 42, bancos: 2 };

  // Load more — fetch next API page and append
  const loadMore = () => {
    const nextPage = apiPage + 1;
    setLoading(true);
    listProperties({
      uf: filters.uf || undefined,
      cidade: filters.ci || undefined,
      tipo: filters.tp || undefined,
      banco: filters.bk || undefined,
      modalidade: filters.md || undefined,
      formaPagamento: filters.cd || undefined,
      nivelRisco: filters.nr || undefined,
      precoMin: filters.pm || undefined,
      precoMax: filters.px || undefined,
      q: searchText || undefined,
      sort: SORT_MAP[sortBy] || 'desconto',
      page: nextPage,
      pageSize: PER_PAGE,
    }).then((res) => {
      setProperties((prev) => [...prev, ...res.items]);
      setApiPage(nextPage);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  const quickFilters = [
    ['Venda Online', { md: 'Venda Online' }],
    ['Venda Direta Online', { md: 'Venda Direta Online' }],
    ['Até R$200k', { px: '200000' }],
    ['Caixa', { bk: 'Caixa' }],
    ['Santander', { bk: 'Santander' }],
    ['Apartamentos', { tp: 'Apartamento' }],
    ['Casas', { tp: 'Casa' }],
  ];

  // ─── HOME VIEW ───
  if (view === 'home') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--tx)', overflowX: 'hidden' }}>

        {/* ── Topbar ── */}
        <header role="banner" className="topbar-glass" style={{ position: 'sticky', top: 0, zIndex: 100, borderBottom: '1px solid var(--bd)' }}>
          <nav className="nav-home" aria-label="Navegação principal" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 28px', maxWidth: 1200, margin: '0 auto' }}>
            <Brand />
            <div className="nav-home-btns" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button onClick={() => setShowSettings(true)} style={{ background: 'transparent', border: '1px solid var(--bd)', borderRadius: 8, width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border-color .15s' }}>
                <Ic name={dark ? 'moon' : 'sun'} s={14} c="var(--br)" />
              </button>
              {user ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button onClick={() => { setView('favorites'); }} style={{ background: 'transparent', border: '1px solid var(--bd)', borderRadius: 8, padding: '6px 11px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, color: 'var(--tx)' }}>
                    <HeartFilled s={12} /> {favorites.size}
                  </button>
                  <button onClick={() => setShowProfile(true)} style={{ background: 'var(--br)', border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Ic name="user" s={12} c="#fff" /> {user.name}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={openLogin} style={{ background: 'transparent', border: '1px solid var(--bd)', borderRadius: 8, padding: '6px 15px', cursor: 'pointer', fontSize: 12, color: 'var(--tx)' }}>Entrar</button>
                  <button onClick={openSignup} style={{ background: 'var(--br)', border: 'none', borderRadius: 8, padding: '6px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#fff' }}>Assinar</button>
                </div>
              )}
            </div>
          </nav>
        </header>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <main id="main-content" role="main">
          {/* ── Hero ── */}
          <section style={{ position: 'relative', maxWidth: 780, margin: '0 auto', textAlign: 'center', padding: '52px 20px 44px', animation: 'fadeIn .7s ease' }}>

            {/* Orb backgrounds */}
            <div className="orb orb-gold" style={{ width: 500, height: 500, top: -120, left: '50%', transform: 'translateX(-50%)', opacity: .6 }} />
            <div className="orb orb-accent" style={{ width: 320, height: 320, top: 60, right: -80, opacity: .5 }} />

            {/* Logo */}
            <div style={{ marginBottom: 20, display: 'inline-flex', padding: '10px', background: 'var(--bb)', borderRadius: '50%', border: '1px solid rgba(184,134,42,.2)' }}>
              <Logo size={48} />
            </div>

            {/* Eyebrow */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--bb)', border: '1px solid rgba(184,134,42,.25)', borderRadius: 20, padding: '4px 14px', fontSize: 11, fontWeight: 600, color: 'var(--br)', letterSpacing: '.8px', textTransform: 'uppercase', marginBottom: 20 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--br)', display: 'inline-block' }} />
              Caixa · Santander · Alertas · IA
            </div>

            <h1 className="hero-title display" style={{ fontSize: 44, fontWeight: 600, lineHeight: 1.1, marginBottom: 18, letterSpacing: '-.5px' }}>
              Imóveis de leilão<br />
              <span className="display-italic" style={{ color: 'var(--br)' }}>em todo o Brasil</span>
            </h1>
            <p className="hero-sub" style={{ fontSize: 15, color: 'var(--t2)', lineHeight: 1.65, maxWidth: 460, margin: '0 auto 32px' }}>
              Caixa e Santander com até 90% de desconto. Análise de risco por IA, alertas personalizados e muito mais.
            </p>

            {/* Search bar */}
            <div className="search-bar" style={{
              display: 'flex', maxWidth: 520, margin: '0 auto 12px',
              background: 'var(--sf)', border: '1.5px solid var(--bd)',
              borderRadius: 'var(--radius)', padding: 4,
              boxShadow: 'var(--shadow-md)',
              transition: 'border-color .2s, box-shadow .2s',
            }}
              onFocusCapture={(e) => { e.currentTarget.style.borderColor = 'var(--br)'; e.currentTarget.style.boxShadow = 'var(--shadow-gold)'; }}
              onBlurCapture={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
            >
              <div style={{ padding: '0 13px', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {loading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16 }}>
                    <div style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      border: '2px solid var(--br)',
                      borderTop: '2px solid transparent',
                      animation: 'spin 1s linear infinite',
                      display: loading ? 'block' : 'none'
                    }} />
                  </div>
                ) : (
                  <Ic name="search" s={16} c="var(--br)" />
                )}
              </div>
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !loading) doSearch(); }}
                placeholder="Cidade, tipo ou banco..."
                style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 14, padding: '10px 0', color: 'var(--tx)', fontFamily: 'var(--font-ui)' }}
                disabled={loading}
              />
              <button
                onClick={doSearch}
                style={{
                  ...primaryButtonStyle,
                  borderRadius: 10,
                  padding: '10px 26px',
                  fontWeight: 700,
                  fontSize: 13,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 70,
                  background: loading ? 'var(--t3)' : primaryButtonStyle.background,
                  cursor: loading ? 'default' : 'pointer',
                }}
                disabled={loading}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.background = 'var(--brl)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.background = 'var(--br)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(184, 134, 42, 0.4), 0 2px 4px rgba(0, 0, 0, 0.1)';
                }}
                onBlur={(e) => {
                  if (!loading) {
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                  }
                }}
              >
                {loading ? (
                  <div style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    border: '2px solid #fff',
                    borderTop: '2px solid transparent',
                    animation: 'spin 1s linear infinite'
                  }} />
                ) : (
                  'Buscar'
                )}
              </button>
            </div>

            {!user && (
              <p style={{ fontSize: 11.5, color: 'var(--t3)', marginBottom: 28 }}>
                {3 - searchCount > 0 ? `${3 - searchCount} busca${3 - searchCount !== 1 ? 's' : ''} gratuita${3 - searchCount !== 1 ? 's' : ''} restante${3 - searchCount !== 1 ? 's' : ''}` : 'Limite atingido'} ·{' '}
                <span style={{ color: 'var(--br)', cursor: 'pointer', fontWeight: 600 }} onClick={() => setShowPaywall(true)}>Assine para acesso ilimitado</span>
              </p>
            )}

            {/* Quick filters */}
            <div className="qf-wrap" style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 52 }}>
              {quickFilters.map(([label, filterObj]) => (
                <button key={label} onClick={() => {
                  // Reset to defaults first — clicking 'Caixa' after
                  // 'Venda Online' should *replace* the modalidade filter,
                  // not stack with it.
                  setFilters({ ...DEFAULT_FILTERS, ...filterObj });
                  setPage(1);
                  if (!user && searchCount >= 3) { setShowPaywall(true); return; }
                  if (!user) setSearchCount((c) => c + 1);
                  setView('results');
                }} style={{
                  background: 'var(--sf)', border: '1px solid var(--bd)',
                  borderRadius: 7, padding: '7px 15px',
                  color: 'var(--t2)', fontSize: 12, cursor: 'pointer', fontWeight: 500,
                  transition: 'border-color .15s, color .15s',
                }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--brl)'; e.currentTarget.style.color = 'var(--br)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.color = 'var(--t2)'; }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Stats */}
            <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, maxWidth: 520, margin: '0 auto' }}>
              {[
                [stats.total + '+', 'Imóveis', 'home'],
                [stats.estados,     'Estados', 'map'],
                [stats.avgDiscount + '%', 'Desconto médio', 'tag'],
                [stats.bancos,      'Bancos', 'shield'],
              ].map(([val, label, icon]) => (
                <div key={label} className="stat-card" style={{
                  background: 'var(--sf)', border: '1px solid var(--bd)',
                  borderRadius: 'var(--radius-sm)', padding: '16px 8px', textAlign: 'center',
                  boxShadow: 'var(--shadow-sm)',
                }}>
                  <Ic name={icon} s={15} c="var(--br)" />
                  <div className="display" style={{ fontSize: 22, fontWeight: 600, marginTop: 5, letterSpacing: '-.5px' }}>{val}</div>
                  <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 2, lineHeight: 1.3 }}>{label}</div>
                </div>
              ))}
            </div>
          </section>
        </main>

        <hr className="gold-divider" style={{ maxWidth: 600, margin: '0 auto 32px', opacity: .25 }} />

        <footer role="contentinfo" style={{ textAlign: 'center', padding: '0 20px 40px' }}>
          <button
            onClick={() => user ? doSearch() : setShowPaywall(true)}
            style={{
              ...primaryButtonStyle,
              padding: '13px 36px',
              borderRadius: 'var(--radius-sm)',
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: '.2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: loading ? 'var(--t3)' : primaryButtonStyle.background,
              cursor: loading ? 'default' : 'pointer',
              transform: loading ? 'none' : undefined,
            }}
            disabled={loading}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.background = 'var(--brl)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.background = 'var(--br)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(184, 134, 42, 0.4), 0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
            onBlur={(e) => {
              if (!loading) {
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            {loading ? (
              <>
                <div style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: '2px solid #fff',
                  borderTop: '2px solid transparent',
                  animation: 'spin 1s linear infinite'
                }} />
                Buscando...
              </>
            ) : (
              user ? 'Explorar imóveis →' : 'Começar gratuitamente →'
            )}
          </button>
        </footer>

        <SiteFooter onOpenContact={() => setShowContact(true)} />

        {showPaywall && <PaywallModal initialTab={paywallTab} onClose={() => setShowPaywall(false)} onLogin={(u) => {
          setUser(u); setShowPaywall(false);
          fetchFavorites().then((items) => { hydrateFavorites(items); });
          fetchSavedFilters().then((items) => { if (items.length) setSavedFilters(items); });
        }} />}
        {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
        {showProfile && user && <ProfileModal user={user} onClose={() => setShowProfile(false)} onLogout={async () => { await authLogout(); setUser(null); setFavorites(new Set()); setFavProps([]); setSavedFilters([]); setShowProfile(false); }} onUpdate={(u) => setUser(u)} savedFilters={savedFilters} onDeleteFilter={(i) => setSavedFilters((prev) => prev.filter((_, idx) => idx !== i))} onUpgrade={() => setShowPaywall(true)} onApplyFilter={(f) => {
          setFilters({ ...DEFAULT_FILTERS, ...fromApiFilters(f.filters) });
          setPage(1);
          setView('results');
          setShowProfile(false);
        }} />}
        {showContact && <ContactModal onClose={() => setShowContact(false)} />}
      </div>
    );
  }

  // ─── RESULTS / FAVORITES VIEW ───
  const displayList = view === 'favorites' ? favList : properties;
  const totalCount = view === 'favorites' ? favList.length : apiTotal;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--tx)' }}>
      {/* Top bar */}
      <header role="banner" className="topbar-glass" style={{ position: 'sticky', top: 0, zIndex: 100, padding: '7px 20px' }}>
        <div className="topbar-inner" style={{ display: 'flex', alignItems: 'center', gap: 10, maxWidth: 1400, margin: '0 auto' }}>
          <button onClick={() => setView('home')} style={{ background: 'transparent', border: '1px solid var(--bd)', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Ic name="back" s={15} c="var(--t2)" />
          </button>
          <div style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => setView('home')}><Brand size="sm" /></div>
          <div className="topbar-search" style={{ flex: 1, maxWidth: 300, margin: '0 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', background: 'var(--sf)', border: '1px solid var(--bd)', borderRadius: 8, padding: '0 9px', transition: 'border-color .2s' }}
              onFocusCapture={(e) => e.currentTarget.style.borderColor = 'var(--br)'}
              onBlurCapture={(e) => e.currentTarget.style.borderColor = 'var(--bd)'}
            >
              <Ic name="search" s={12} c="var(--br)" />
              <input value={searchText} onChange={(e) => { setSearchText(e.target.value); setPage(1); }} placeholder="Buscar..."
                style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 12, padding: '7px 7px', color: 'var(--tx)', fontFamily: 'var(--font-ui)' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 5, marginLeft: 'auto', alignItems: 'center' }}>
            <button onClick={() => setShowSettings(true)} style={{ background: 'transparent', border: '1px solid var(--bd)', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Ic name={dark ? 'moon' : 'sun'} s={13} c="var(--br)" />
            </button>
            <button onClick={() => setView(view === 'favorites' ? 'results' : 'favorites')} style={{ background: view === 'favorites' ? 'var(--bb)' : 'transparent', border: '1px solid ' + (view === 'favorites' ? 'var(--br)' : 'var(--bd)'), borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, color: view === 'favorites' ? 'var(--br)' : 'var(--tx)' }}>
              <HeartFilled s={11} /> {favorites.size}
            </button>
            {user ? (
              <button className="hide-mobile" onClick={() => setShowProfile(true)} style={{ background: 'var(--br)', border: 'none', borderRadius: 8, padding: '5px 11px', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, color: '#fff' }}>
                <Ic name="user" s={11} c="#fff" /> {user.name}
              </button>
            ) : (
              <button onClick={() => setShowPaywall(true)} style={{ background: 'var(--br)', border: 'none', borderRadius: 8, padding: '6px 13px', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#fff' }}>Assinar</button>
            )}
          </div>
        </div>
      </header>

      <main role="main" className="main-content" style={{ maxWidth: 1400, margin: '0 auto', padding: '16px 20px', display: 'flex', gap: 16 }}>
        {/* Filter backdrop (mobile/tablet only) */}
        {view === 'results' && showFilters && (
          <div className="filter-backdrop" onClick={() => setShowFilters(false)} />
        )}

        {/* Sidebar filters — always visible on desktop, toggle on mobile/tablet */}
        {view === 'results' && (
          <aside aria-label="Filtros de busca" className={'sidebar-filters' + (showFilters ? ' sidebar-filters-open' : '')} style={{ width: 220, flexShrink: 0, background: 'var(--sf)', border: '1px solid var(--bd)', borderRadius: 'var(--radius)', padding: 14, height: 'fit-content', position: 'sticky', top: 60, boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, letterSpacing: '.3px', textTransform: 'uppercase', color: 'var(--t2)' }}>
                <Ic name="filter" s={12} c="var(--br)" /> Filtros
              </h3>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setFilters(DEFAULT_FILTERS)} style={{ background: 'none', border: 'none', color: 'var(--br)', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Limpar</button>
                <button className="filter-close-btn" onClick={() => setShowFilters(false)} style={{ background: 'var(--s2)', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', display: 'none', alignItems: 'center', justifyContent: 'center' }}>
                  <Ic name="x" s={12} c="var(--t3)" />
                </button>
              </div>
            </div>
            <hr className="gold-divider" style={{ marginBottom: 12, opacity: .2 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <select value={filters.uf} onChange={(e) => setFilters((f) => ({ ...f, uf: e.target.value, ci: '' }))} style={inputStyle}>
                <option value="">Estado</option>{UFS.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
              {cities.length > 0 && (
                <select value={filters.ci} onChange={(e) => updateFilter('ci', e.target.value)} style={inputStyle}>
                  <option value="">Cidade</option>{cities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
              <select value={filters.tp} onChange={(e) => updateFilter('tp', e.target.value)} style={inputStyle}>
                <option value="">Tipo</option>{TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={filters.bk} onChange={(e) => updateFilter('bk', e.target.value)} style={inputStyle}>
                <option value="">Banco</option>{BANCOS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <select value={filters.md} onChange={(e) => updateFilter('md', e.target.value)} style={inputStyle}>
                <option value="">Modalidade</option>
                {MODALIDADES.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
              </select>
              <select value={filters.cd} onChange={(e) => updateFilter('cd', e.target.value)} style={inputStyle}>
                <option value="">Forma de pagamento</option>
                {FORMAS_PAGAMENTO.map((fp) => <option key={fp.code} value={fp.code}>{fp.label}</option>)}
              </select>
              <select value={filters.nr} onChange={(e) => updateFilter('nr', e.target.value)} style={inputStyle}>
                <option value="">Nível de risco</option>
                {NIVEIS_RISCO.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
              </select>
              <div>
                <label style={{ fontSize: 10, color: 'var(--t3)', display: 'block', marginBottom: 2 }}>Preço</label>
                <div style={{ display: 'flex', gap: 3 }}>
                  <input type="number" placeholder="Mín" value={filters.pm} onChange={(e) => updateFilter('pm', e.target.value)} style={inputStyle} />
                  <input type="number" placeholder="Máx" value={filters.px} onChange={(e) => updateFilter('px', e.target.value)} style={inputStyle} />
                </div>
              </div>
              <select value={filters.qm} onChange={(e) => updateFilter('qm', e.target.value)} style={inputStyle}>
                <option value="">Quartos</option><option value="1">1+</option><option value="2">2+</option><option value="3">3+</option><option value="4">4+</option>
              </select>
              <button onClick={() => { if (!user && searchCount >= 3) { setShowPaywall(true); return; } setShowSave(true); }}
                style={{ padding: 7, borderRadius: 7, border: '1px solid var(--br)', background: 'transparent', color: 'var(--br)', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>
                Salvar/Alerta
              </button>
              <button className="filter-apply-btn" onClick={() => setShowFilters(false)}
                style={{ padding: 10, borderRadius: 8, border: 'none', background: 'var(--br)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginTop: 4 }}>
                Aplicar Filtros ({apiTotal} imóveis)
              </button>
            </div>

            {/* Saved filters list */}
            {savedFilters.length > 0 && (
              <div style={{ marginTop: 12, borderTop: '1px solid var(--bd)', paddingTop: 10 }}>
                <h4 style={{ fontSize: 11, fontWeight: 600, color: 'var(--t3)', marginBottom: 6 }}>
                  <Ic name="bell" s={11} c="var(--t3)" /> Alertas Salvos
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {savedFilters.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', background: 'var(--s2)', borderRadius: 7, fontSize: 11 }}>
                      <span style={{ color: 'var(--tx)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, cursor: 'pointer' }}
                        onClick={() => { setFilters({ ...DEFAULT_FILTERS, ...fromApiFilters(f.filters) }); setPage(1); }}>
                        {f.name}
                      </span>
                      <button onClick={async () => { if (f?.id) await deleteSavedFilter(f.id); setSavedFilters((prev) => prev.filter((_, idx) => idx !== i)); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0 }}>
                        <Ic name="x" s={10} c="var(--t3)" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>
        )}

        {/* Main content */}
        <section style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <h2 className="display" style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-.3px' }}>{view === 'favorites' ? 'Favoritos' : 'Resultados'}</h2>
              <p style={{ fontSize: 11, color: 'var(--t3)', marginTop: 1 }}>{totalCount} imóveis encontrados</p>
            </div>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
              {view === 'results' && (
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                  <button className="filter-toggle-btn" onClick={() => setShowFilters(!showFilters)} style={{ background: showFilters ? 'var(--bb)' : 'var(--sf)', border: '1px solid ' + (showFilters ? 'var(--br)' : 'var(--bd)'), borderRadius: 8, padding: '7px 10px', cursor: 'pointer', display: 'none', alignItems: 'center' }}>
                    <Ic name="filter" s={12} c={showFilters ? 'var(--br)' : 'var(--t3)'} />
                  </button>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ padding: '6px 8px', borderRadius: 7, border: '1px solid var(--bd)', background: 'var(--sf)', fontSize: 12, color: 'var(--tx)' }}>
                    <option value="dc">Maior desc.</option><option value="pa">Menor preço</option><option value="pd">Maior preço</option><option value="dt">Data</option><option value="ar">Maior área</option>
                  </select>
                </div>
              )}
              <div style={{ display: 'flex', background: 'var(--sf)', border: '1px solid var(--bd)', borderRadius: 7, overflow: 'hidden' }}>
                <button onClick={() => setViewMode('grid')} style={{ padding: '6px 8px', border: 'none', cursor: 'pointer', background: viewMode === 'grid' ? 'var(--bb)' : 'transparent' }}>
                  <Ic name="grid" s={12} c={viewMode === 'grid' ? 'var(--br)' : 'var(--t3)'} />
                </button>
                <button onClick={() => setViewMode('list')} style={{ padding: '6px 8px', border: 'none', cursor: 'pointer', background: viewMode === 'list' ? 'var(--bb)' : 'transparent' }}>
                  <Ic name="list" s={12} c={viewMode === 'list' ? 'var(--br)' : 'var(--t3)'} />
                </button>
              </div>
            </div>
          </div>

          {displayList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
              <Ic name="search" s={36} c="var(--t3)" />
              <p style={{ fontSize: 14, color: 'var(--t2)', marginTop: 12 }}>{view === 'favorites' ? 'Nenhum favorito.' : 'Nenhum resultado.'}</p>
              {view === 'favorites' && (
                <button onClick={() => setView('results')} style={{ marginTop: 10, padding: '8px 20px', borderRadius: 7, border: 'none', background: 'var(--br)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Explorar</button>
              )}
            </div>
          ) : (
            <div>
              <div className="cards-grid" style={{ display: viewMode === 'grid' ? 'grid' : 'flex', gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(235px, 1fr))' : undefined, flexDirection: viewMode === 'list' ? 'column' : undefined, gap: 10 }}>
                {displayList.map((p) => (
                  <PropertyCard key={p.id} property={p} favorites={favorites} toggleFav={toggleFav} viewMode={viewMode} enriched={enrichedIds.has(p.id)} />
                ))}
              </div>

              {/* Paginação */}
              {view === 'results' && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 24, flexWrap: 'wrap' }}>
                  {page > 1 && (
                    <button
                      onClick={() => setPage(page - 1)}
                      style={{
                        padding: '8px 12px', borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--bd)', background: 'var(--sf)',
                        color: 'var(--tx)', fontWeight: 600, fontSize: 12,
                        cursor: 'pointer', transition: 'border-color .15s, color .15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--br)'; e.currentTarget.style.color = 'var(--br)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.color = 'var(--tx)'; }}
                    >
                      Anterior
                    </button>
                  )}

                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {(() => {
                      const totalPages = Math.ceil(apiTotal / PER_PAGE);

                      if (totalPages <= 5) {
                        // Mostrar todas as páginas se forem 5 ou menos
                        return Array.from({ length: totalPages }, (_, i) => {
                          const pageNum = i + 1;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => {
                                setPage(pageNum);
                                // Buscar dados para a nova página
                                setLoading(true);
                                listProperties({
                                  uf: filters.uf || undefined,
                                  cidade: filters.ci || undefined,
                                  tipo: filters.tp || undefined,
                                  banco: filters.bk || undefined,
                                  modalidade: filters.md || undefined,
                                  precoMin: filters.pm || undefined,
                                  precoMax: filters.px || undefined,
                                  q: searchText || undefined,
                                  sort: SORT_MAP[sortBy] || 'desconto',
                                  page: pageNum,
                                  pageSize: PER_PAGE,
                                }).then((res) => {
                                  setProperties(res.items);
                                  setApiPage(pageNum);
                                }).catch(() => {}).finally(() => setLoading(false));
                              }}
                              style={{
                                padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--bd)',
                                background: page === pageNum ? 'var(--br)' : 'var(--sf)',
                                color: page === pageNum ? '#fff' : 'var(--tx)',
                                fontWeight: 600, fontSize: 12,
                                cursor: 'pointer', transition: 'all .15s',
                                minWidth: 36,
                              }}
                              onMouseEnter={(e) => { if (page !== pageNum) { e.currentTarget.style.borderColor = 'var(--br)'; e.currentTarget.style.color = 'var(--br)'; } }}
                              onMouseLeave={(e) => { if (page !== pageNum) { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.color = 'var(--tx)'; } }}
                            >
                              {pageNum}
                            </button>
                          );
                        });
                      } else {
                        // Para muitas páginas, mostra padrão com elipses
                        const pagesToShow = [];
                        const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
                        const endPage = Math.min(totalPages, startPage + 4);

                        if (startPage > 1) {
                          pagesToShow.push(1);
                          if (startPage > 2) pagesToShow.push('...');
                        }

                        for (let i = startPage; i <= endPage; i++) {
                          pagesToShow.push(i);
                        }

                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) pagesToShow.push('...');
                          pagesToShow.push(totalPages);
                        }

                        return pagesToShow.map((num, idx) => (
                          num === '...' ? (
                            <span key={`dots-${idx}-${Math.random()}`} style={{ padding: '6px 4px', fontSize: 12, color: 'var(--t3)' }}>...</span>
                          ) : (
                            <button
                              key={num}
                              onClick={() => {
                                setPage(num);
                                // Buscar dados para a nova página
                                setLoading(true);
                                listProperties({
                                  uf: filters.uf || undefined,
                                  cidade: filters.ci || undefined,
                                  tipo: filters.tp || undefined,
                                  banco: filters.bk || undefined,
                                  modalidade: filters.md || undefined,
                                  precoMin: filters.pm || undefined,
                                  precoMax: filters.px || undefined,
                                  q: searchText || undefined,
                                  sort: SORT_MAP[sortBy] || 'desconto',
                                  page: num,
                                  pageSize: PER_PAGE,
                                }).then((res) => {
                                  setProperties(res.items);
                                  setApiPage(num);
                                }).catch(() => {}).finally(() => setLoading(false));
                              }}
                              style={{
                                padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--bd)',
                                background: page === num ? 'var(--br)' : 'var(--sf)',
                                color: page === num ? '#fff' : 'var(--tx)',
                                fontWeight: 600, fontSize: 12,
                                cursor: 'pointer', transition: 'all .15s',
                                minWidth: 36,
                              }}
                              onMouseEnter={(e) => { if (page !== num) { e.currentTarget.style.borderColor = 'var(--br)'; e.currentTarget.style.color = 'var(--br)'; } }}
                              onMouseLeave={(e) => { if (page !== num) { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.color = 'var(--tx)'; } }}
                            >
                              {num}
                            </button>
                          )
                        ));
                      }
                    })()}
                  </div>

                  {page < Math.ceil(apiTotal / PER_PAGE) && (
                    <button
                      onClick={() => setPage(page + 1)}
                      style={{
                        padding: '8px 12px', borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--bd)', background: 'var(--sf)',
                        color: 'var(--tx)', fontWeight: 600, fontSize: 12,
                        cursor: 'pointer', transition: 'border-color .15s, color .15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--br)'; e.currentTarget.style.color = 'var(--br)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.color = 'var(--tx)'; }}
                    >
                      Próxima
                    </button>
                  )}

                  <div style={{ marginLeft: 12, fontSize: 12, color: 'var(--t3)' }}>
                    Página {page} de {Math.ceil(apiTotal / PER_PAGE)} ({apiTotal} resultados)
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </main>

      <SiteFooter />

      {/* All modals */}
      {showCalc && <CalculatorModal property={calcProp} onClose={() => setShowCalc(false)} />}
      {showAI && aiProp && <AIAnalysisModal property={aiProp} onClose={() => setShowAI(false)} />}
      {showSave && <SaveFilterModal filters={filters} user={user} onClose={() => setShowSave(false)} onSave={async (f) => {
        const saved = await createSavedFilter(f);
        setSavedFilters((prev) => [...prev, saved ?? f]);
      }} />}
      {showPaywall && <PaywallModal initialTab={paywallTab} onClose={() => setShowPaywall(false)} onLogin={(u) => {
        setUser(u); setShowPaywall(false);
        fetchFavorites().then((items) => { hydrateFavorites(items); });
        fetchSavedFilters().then((items) => { if (items.length) setSavedFilters(items); });
      }} />}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      {showProfile && user && <ProfileModal user={user} onClose={() => setShowProfile(false)} onLogout={async () => { await authLogout(); setUser(null); setFavorites(new Set()); setFavProps([]); setSavedFilters([]); setShowProfile(false); setView('home'); }} onUpdate={(u) => setUser(u)} savedFilters={savedFilters} onDeleteFilter={async (i) => {
        const f = savedFilters[i];
        if (f?.id) await deleteSavedFilter(f.id);
        setSavedFilters((prev) => prev.filter((_, idx) => idx !== i));
      }} onUpgrade={() => setShowPaywall(true)} onApplyFilter={(f) => {
        setFilters({ ...DEFAULT_FILTERS, ...fromApiFilters(f.filters) });
        setPage(1);
        setView('results');
        setShowProfile(false);
      }} />}
      {showContact && <ContactModal onClose={() => setShowContact(false)} />}
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AppInner />
    </ThemeProvider>
  );
}
